import type { BillingInterval, Plan } from "../../../shared/types.js";

/**
 * Context that rides along every hop between /plans and /checkout: inside the demo iframe
 * (`embed`), in the buyer's language, and with the campaign that brought them (`utm_*`, which the
 * checkout stores on the customer). Dropping it put the marketing header back inside the iframe
 * and lost the traffic source of every link that pointed at the plans page.
 */
export function copyNavigationContext(from: URLSearchParams, to: URLSearchParams): URLSearchParams {
  for (const key of ["embed", "app_language"]) {
    const value = from.get(key);
    if (value) to.set(key, value);
  }
  for (const [key, value] of from) {
    if (key.startsWith("utm_") && value) to.set(key, value);
  }
  return to;
}

/** What the buyer already told us on the way in, prefilled on the checkout form. */
const BUYER_FIELDS = ["email", "name", "sales_token", "phone", "whatsapp", "cpf", "country"] as const;

/**
 * /checkout URL for the plan chosen on the plans page. An offer link only charges monthly, so its
 * interval is forced; everything the buyer brought in the URL goes along.
 */
export function buildCheckoutUrlFromPlans(search: URLSearchParams, plan: Plan, interval: BillingInterval): string {
  const offer = search.get("offer");
  const params = new URLSearchParams({ plan, interval: offer ? "monthly" : interval });
  for (const key of BUYER_FIELDS) {
    const value = search.get(key);
    if (value) params.set(key, value);
  }
  if (search.get("phone_locked") === "1") params.set("phone_locked", "1");
  if (offer) params.set("offer", offer);
  // An add-on the link asks to open ticked.
  const bump = search.get("bump");
  if (bump) params.set("bump", bump);
  copyNavigationContext(search, params);
  return `/checkout?${params.toString()}`;
}

/** Clarity event for a plan chosen on the plans page, e.g. `plans_choose_premium_annual`. */
export function plansChooseEventName(plan: Plan, interval: BillingInterval): string {
  return `plans_choose_${plan}_${interval}`;
}

/** Where a plan was chosen on the plans page, sent to Clarity as the `plans_choose_source` tag. */
export type PlansChooseSource = "card" | "table" | "table_header" | "sticky";

/**
 * How the comparison table chooses a plan on the plans page. An offer link keeps its row of
 * "Escolher" buttons under the table. Without an offer that row gave way to the sticky bar on
 * 2026-10-09, and buyers then tapped the plan's column header, which stays on screen while they
 * scroll, expecting it to choose (PIT-457): there the header chooses.
 */
export function comparisonTableChooseProps(
  withOffer: boolean,
  choosePlan: (plan: Plan, source: PlansChooseSource) => void,
): { onChoosePlan?: (plan: Plan) => void; onChooseFromHeader?: (plan: Plan) => void } {
  return withOffer
    ? { onChoosePlan: (plan) => choosePlan(plan, "table") }
    : { onChooseFromHeader: (plan) => choosePlan(plan, "table_header") };
}
